import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { CustomEventModel } from '@models/custom-event.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiNoDataComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { createEventTagsResultTableConfig } from './event-tags-result-table.util';

type EventTag = IntelligenceModels.EventTags.EventTag;

/**
 * Context-bar crumb for the editor opened by a row's Edit icon. Panel labels are
 * plain strings throughout this service (`'Create event tag'`, `'4 event tags'`),
 * not translation keys — this one follows that convention.
 */
const EDIT_OVERLAY_LABEL = 'Edit event tag';

/**
 * Cmd+K table of every organization event tag, modelled on the Database → Event
 * tags page. Filtering is client-side: `list-event-tags` returns the whole
 * organization list in one response, so there is nothing to re-query.
 *
 * Creating and editing both open the `create-event-tag` overlay in place — the
 * one editor, seeded blank or with a hydrated document. Deleting confirms first,
 * then dispatches the page's own `removeCustomEvent`.
 */
@Component({
  selector: 'app-event-tags-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiSearchComponent,
  ],
  templateUrl: './event-tags-result.component.html',
  styleUrls: ['./event-tags-result.component.scss'],
})
export class EventTagsResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly intelligenceToolsService = inject(IntelligenceToolsService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly utilsService = inject(UtilsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);

  protected readonly noData: typeof features.customEvents.noData = features.customEvents.noData;
  protected readonly noResults: typeof features.customEvents.noResults = features.customEvents.noResults;
  protected readonly searchQuery: WritableSignal<string> = signal<string>('');

  protected readonly tableConfig: DatasetTableConfig<EventTag> = createEventTagsResultTableConfig();

  protected readonly response: Signal<IntelligenceModels.EventTags.ListResponse | null> = computed(() => {
    const result = this.resultsService.current();
    return result?.type === 'event-tags'
      ? (result.data as IntelligenceModels.EventTags.ListResponse)
      : null;
  });

  protected readonly eventTags: Signal<EventTag[]> = computed(() => this.response()?.eventTags ?? []);

  protected readonly filteredEventTags: Signal<EventTag[]> = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const eventTags = this.eventTags();
    return query
      ? eventTags.filter((eventTag) => (eventTag.name ?? '').toLowerCase().includes(query))
      : eventTags;
  });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'EVENT_TAGS_RESULT.OPEN_PAGE',
        onClick: () => this.openEventTagsPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  protected onRowAction(event: IntelligenceModels.EventTags.RowActionEvent): void {
    if (event.actionId === IntelligenceModels.EventTags.RowActionId.Edit) {
      void this.editEventTag(event.row);
    } else if (event.actionId === IntelligenceModels.EventTags.RowActionId.Delete) {
      void this.deleteEventTag(event.row);
    }
  }

  /**
   * Show the editor over this list, unseeded — the "+" names nothing, unlike the
   * create-event-tag tool. The overlay adds no history entry, so the context
   * bar's `‹` crumb comes back here unchanged.
   */
  protected openCreatePanel(): void {
    this.resultsService.setCreateEventTag({
      customEvent: IntelligenceModels.EventTags.blankCustomEvent(),
    });
  }

  /**
   * Hydrate the row's whole document, then open the same editor on it. The
   * listing carries only `_id`/`name`, so the video length and the field rows
   * have to be fetched before the form can show what it will overwrite.
   */
  private async editEventTag(row: EventTag): Promise<void> {
    if (!row._id) return;

    const customEvent = await this.hydrate(row._id);
    // Seeding a blank form here would silently turn an edit into a create.
    if (!customEvent) return;

    // The crumb has to name what the overlay is now doing; its default says
    // "Create event tag", which an edit must not be labelled as.
    this.resultsService.setCreateEventTag({ customEvent }, EDIT_OVERLAY_LABEL);
  }

  /**
   * Confirm, then dispatch the page's own delete. The effect has no failure
   * action — it surfaces the server error as a toast — so a toast arriving first
   * settles the flow without removing anything from the panel.
   */
  private async deleteEventTag(row: EventTag): Promise<void> {
    if (!row._id) return;

    const confirmation = await firstValueFrom(
      this.confirmDialogService.open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('EVENT_TAGS_RESULT.DELETE.TITLE'),
        msg: this.translateService.instant('EVENT_TAGS_RESULT.DELETE.MESSAGE', {
          name: `"${row.name}"`,
        }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      }),
    );
    if (confirmation?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;

    // The action carries the whole document, exactly as the page's row does.
    const customEvent = await this.hydrate(row._id);
    if (!customEvent) return;

    const eventTagId = row._id;
    this.actions$
      .pipe(
        ofType(CustomEventsActions.removeCustomEventSuccess),
        filter((action) => action.customEvent === customEvent),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resultsService.removeEventTagFromCurrent(eventTagId));

    this.store$.dispatch(CustomEventsActions.removeCustomEvent({ customEvent }));
  }

  /** Fetch one full document, reporting a miss or a failure as a toast. */
  private async hydrate(eventTagId: string): Promise<CustomEventModel.CustomEvent | null> {
    try {
      const customEvent = await this.intelligenceToolsService.fetchEventTag(eventTagId);
      if (!customEvent) {
        this.store$.dispatch(
          SharedActions.showMessage({
            error: this.translateService.instant('EVENT_TAGS_RESULT.NOT_FOUND'),
          }),
        );
        return null;
      }
      return customEvent;
    } catch (err) {
      this.store$.dispatch(
        SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }),
      );
      return null;
    }
  }

  /** Open the page in a NEW TAB so the current Cmd+K session stays open. */
  private openEventTagsPage(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, routerSegments.eventTypes]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
